import { useState, useMemo, useCallback, useEffect, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore, APP_MODE_CONFIGS, type ManagementContext, type AppMode } from '@/store/appStore'
import { useProjectEntitlementStore } from '@/config/projectEntitlements'
import { usePortfolioHierarchy, useProjects, usePortfolios } from '@/hooks/usePortfolioData'
import { useSandboxOrgOptions } from '@/hooks/useSandboxOrgOptions'
import { APP_VERSION_FULL } from '@/constants/version'
import { DEMO_PROFILES } from '@/types/demoProfiles'
import { companiesForProfile, getDemoCompany } from '@/types/demoCompanies'
import { markCompanyPicked } from '@/modules/timesheet/lib/companyPickIntent'
import type { Portfolio, Project } from '@/types/ppm'
import { cn } from '@/lib/utils'
import { formatCompactCurrency, getStatusBadgeClass } from '@/lib/utils'
import { Button } from '@/components/common/Button'
import { SandboxQuickStart } from '@/components/onboarding/SandboxQuickStart'
import { AccountMenu } from '@/components/auth/AccountMenu'
import { APP_IDENTITY, appFullName } from '@/config/appIdentity'
import { isContextVisible, useModuleEntitlement } from '@/config/moduleEntitlements'
import {
  ChevronRight,
  ChevronDown,
  FolderTree,
  Briefcase,
  TrendingUp,
  Shield,
  Sparkles,
  FolderKanban,
  FileText,
  Building2,
  ArrowLeft,
  ArrowLeftRight,
  HelpCircle,
  X,
  Search,
  Calendar,
  DollarSign,
  Activity,
  Workflow,
  User,
  Filter,
  Play,
  Box,
  Code2,
  ShieldCheck,
  Rocket,
  Check,
  Monitor,
  Plus,
  HardHat,
  Calculator,
  Handshake,
  Database,
  Settings,
  PackageSearch,
  Clock,
  Inbox,
  Eye,
  ChartNoAxesCombined,
  Ticket,
} from 'lucide-react'
import { isInternalSurface } from '@/config/productSurface'
import { subscribePreviewClient, getPreviewClientKey } from '@/config/previewClient'
import { enterClientPreview, exitClientPreview } from './previewClientActions'

const MODE_ICONS: Record<AppMode, React.ReactNode> = {
  demo: <Play size={16} />,
  sandbox: <Box size={16} />,
  development: <Code2 size={16} />,
  quality: <ShieldCheck size={16} />,
  production: <Rocket size={16} />,
}

const MODE_ORDER: AppMode[] = ['demo', 'sandbox', 'development', 'quality', 'production']

function ContextModeSwitcher() {
  const appMode = useAppStore((s) => s.appMode)
  const setAppMode = useAppStore((s) => s.setAppMode)
  // A project-restricted user (outside tester) stays in their own account and environment.
  const restricted = useProjectEntitlementStore((s) => s.projectIds !== null)
  if (restricted) return null

  // Header/badge live in the tunnel header above (WorkspacePanel) — the rail
  // itself is labeled by its Lifecycle panel, so no duplicate title here.
  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {MODE_ORDER.map((mode) => {
          const mc = APP_MODE_CONFIGS[mode]
          const isActive = mode === appMode
          return (
            <button
              key={mode}
              onClick={() => setAppMode(mode)}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
                isActive
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : 'border-[#2a3f60] bg-[#101d31] text-[#a9bcda] hover:bg-[#16273f] hover:border-[#3a5680]'
              )}
            >
              <span className="flex-shrink-0">{MODE_ICONS[mode]}</span>
              <span>{mc.label}</span>
              {isActive && <Check size={11} />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const PROFILE_ICONS: Record<string, React.ReactNode> = {
  Monitor: <Monitor size={14} />,
  HardHat: <HardHat size={14} />,
  Rocket: <Rocket size={14} />,
}

function DemoProfilePicker() {
  const appMode = useAppStore((s) => s.appMode)
  const demoProfileId = useAppStore((s) => s.demoProfileId)
  const setDemoProfile = useAppStore((s) => s.setDemoProfile)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const setCurrentCompany = useAppStore((s) => s.setCurrentCompany)
  const selectPortfolio = useAppStore((s) => s.selectPortfolio)
  const { data: portfolios } = usePortfolios()
  const navigate = useNavigate()

  const companies = companiesForProfile(demoProfileId)

  // Pick a company: scope the management context to it + jump into its portfolio.
  const pickCompany = (companyId: string) => {
    const company = getDemoCompany(companyId)
    // The CHOICE is made — collapse the tunnel disclosure back to the chip.
    orgPanelOpenPersist = false
    // Mark this as a deliberate company switch so the persona switcher re-points
    // the persona to the new company's cast (vs a refresh, where the persona wins).
    markCompanyPicked(companyId)
    setCurrentCompany(companyId)
    if (company) {
      const root = (portfolios ?? []).find((p) => p.id === company.rootPortfolioId)
      if (root) selectPortfolio(root)
    }
    navigate('/')
  }

  // Picking a demo profile signs in to a shared demo account: never offered to a
  // project-restricted user (it would step around their project list).
  const restricted = useProjectEntitlementStore((s) => s.projectIds !== null)

  // Renders in every mode EXCEPT sandbox (which has its own real-org picker):
  // the workspace identity is defined outside any one mode — the tunnel.
  if (appMode === 'sandbox' || restricted) return null
  const isDemo = appMode === 'demo'

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Database size={14} className="text-[#7d93b8]" />
        <h3 className="text-[12px] font-display font-semibold text-[#cfe0f5]">{isDemo ? 'Demo Profile' : 'Industry Profile'}</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
        {DEMO_PROFILES.map((profile) => {
          const isActive = profile.id === demoProfileId
          return (
            <button
              key={profile.id}
              onClick={() => { setDemoProfile(profile.id); navigate('/') }}
              className={cn(
                'text-left flex items-center gap-2 px-2 py-1.5 rounded-md border transition-all duration-200',
                isActive
                  ? 'border-brand-400 bg-brand-500/20 ring-1 ring-brand-400/40'
                  : 'border-[#2a3f60] bg-[#101d31] hover:bg-[#16273f] hover:border-[#3a5680]'
              )}
            >
              <div className={cn(
                'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
                isActive ? 'bg-brand-500/30 text-cyan-200' : 'bg-[#16273f] text-[#7d93b8]'
              )}>
                {PROFILE_ICONS[profile.icon] ?? <Monitor size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={cn('text-[11px] font-semibold truncate', isActive ? 'text-white' : 'text-[#e8f0fc]')} title={profile.description}>
                  {profile.name}
                </div>
                <div className="text-[10px] text-[#7d93b8] truncate">
                  {profile.industry}
                </div>
              </div>
              {isActive && <Check size={11} className="text-cyan-300 flex-shrink-0" />}
            </button>
          )
        })}
      </div>

      {/* Demo company picker — scopes the management context to one company (#67-lite). */}
      <div className="mt-3 border-t border-[#22334f] pt-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Building2 size={13} className="text-[#7d93b8]" />
            <h3 className="text-[12px] font-display font-semibold text-[#cfe0f5]">{isDemo ? 'Demo Company' : 'Organization'}</h3>
          </div>
          {currentCompanyId && (
            <button onClick={() => setCurrentCompany(null)} className="text-[10px] text-[#7d93b8] hover:text-white">Show all</button>
          )}
        </div>
        {companies.length === 0 ? (
          <p className="text-[11px] text-[#7d93b8]">No demo companies for this industry yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {companies.map((company) => {
              const isActive = company.id === currentCompanyId
              return (
                <button
                  key={company.id}
                  onClick={() => pickCompany(company.id)}
                  className={cn(
                    'text-left flex items-start gap-2 px-2 py-1.5 rounded-md border transition-all',
                    isActive ? 'border-brand-400 bg-brand-500/20 ring-1 ring-brand-400/40'
                    : company.featured ? 'border-amber-400/50 bg-amber-400/10 hover:bg-amber-400/15'
                    : 'border-[#2a3f60] bg-[#101d31] hover:bg-[#16273f] hover:border-[#3a5680]',
                  )}
                >
                  <Building2 size={13} className={cn('mt-0.5 flex-shrink-0', isActive ? 'text-cyan-300' : company.featured ? 'text-amber-300' : 'text-[#7d93b8]')} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <div className={cn('text-[11px] font-semibold truncate', isActive ? 'text-white' : 'text-[#e8f0fc]')}>{company.name}</div>
                      {company.featured && <span className="text-[8px] uppercase tracking-wide font-bold text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded px-1 flex-shrink-0">Platform</span>}
                    </div>
                    <div className="text-[10px] text-[#7d93b8] line-clamp-1" title={company.blurb}>{company.blurb}</div>
                  </div>
                  {isActive && <Check size={11} className="text-cyan-300 flex-shrink-0 mt-0.5" />}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * WorkspacePanel — slice 2 of the homepage IA blueprint. Fuses the two former
 * side-by-side cards (environment + org) into ONE workspace control so they read
 * as a single "where am I" unit, not two disconnected panels. Environment is the
 * outer axis (it gates which orgs exist), so it sits on top; the org/company
 * picker nests below it with a divider (and self-hides outside demo mode).
 */
// Survives WorkspacePanel remounts (profile picks navigate + remount): the org
// disclosure's open state. True while the user is browsing choices; cleared on
// the actual company/org pick (see pickCompany + the sandbox onPick wrapper).
let orgPanelOpenPersist = false

function WorkspacePanel({
  sandboxOrgOptions,
  sandboxOrgsLoading,
  activeOrganizationId,
  onPickSandboxOrg,
}: {
  sandboxOrgOptions: Array<{ id: string; name: string }>
  sandboxOrgsLoading: boolean
  activeOrganizationId: string | null
  onPickSandboxOrg: (id: string | null) => void
}) {
  // Org-first ("the tunnel", #115 / Tunnel_OrgFirst_Package_2026-07-17):
  // the ORG axis is the outermost frame — a tunnel header names WHO you're
  // inside, the org content picks it, and the mode rail nests beneath as that
  // org's lifecycle in an inset panel. Layout/visual only (P2-lite): no
  // scope-variable changes (P1 stays with the #116 session), all 5 modes
  // untouched.
  const appMode = useAppStore((s) => s.appMode)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  // The workspace is defined in EVERY mode (Connor 2026-06-25 / 2026-07-17):
  // sandbox picks a real org; every other mode picks the company identity.
  const hasOrgSection = true
  const modeConfig = APP_MODE_CONFIGS[appMode]
  // Tunnel identity continuity: the org you picked FRAMES every mode. Demo and
  // sandbox have their own pickers; dev/quality/production inherit the last
  // picked identity instead of dropping to "no org" — that's the tunnel promise
  // (data-level scoping for those modes lands with the P1 scope collapse).
  const demoCompanyName = currentCompanyId
    ? getDemoCompany(currentCompanyId)?.name ?? currentCompanyId
    : null
  const sandboxOrgName = sandboxOrgOptions.find((o) => o.id === activeOrganizationId)?.name ?? null
  const orgLabel =
    appMode === 'demo'
      ? (demoCompanyName ?? 'No company selected')
      : appMode === 'sandbox'
        // Sandbox inherits the picked identity too (the tunnel carries through
        // ALL modes); picking a real sandbox org below swaps it.
        ? (sandboxOrgName ?? demoCompanyName ?? 'No org selected')
        : (demoCompanyName ?? sandboxOrgName ?? 'Mach12 — internal instance')
  // Disclosure: the org tiles live BEHIND the chip (compact like the mock).
  // Starts open only when nothing is selected yet, so first-time users see the
  // choices. The open state persists across remounts (module-scoped) because
  // picking a Demo PROFILE navigates + remounts this panel — the disclosure
  // must stay open while browsing and collapse only on the actual CHOICE
  // (a company / org pick), not before.
  const nothingSelected = appMode === 'sandbox' ? !activeOrganizationId : !currentCompanyId
  const [orgOpen, setOrgOpenState] = useState(() => orgPanelOpenPersist || nothingSelected)
  const setOrgOpen = (v: boolean | ((prev: boolean) => boolean)) => {
    setOrgOpenState((prev) => {
      const next = typeof v === 'function' ? v(prev) : v
      orgPanelOpenPersist = next
      return next
    })
  }
  return (
    <div className="space-y-2.5">
      {/* Tunnel bar — the org identity IS the frame (dark canvas, cyan edge). */}
      <div className="rounded-xl border-[1.5px] border-cyan-400/70 bg-gradient-to-b from-[#0f2033] to-[#0d1a2c] p-3.5">
        <div className="flex items-center gap-3 flex-wrap">
          {hasOrgSection ? (
            <button
              type="button"
              onClick={() => setOrgOpen((v) => !v)}
              aria-expanded={orgOpen}
              className="inline-flex items-center gap-2 rounded-lg border border-[#33507a] bg-[#16273f] px-3 py-2 text-[13.5px] font-semibold text-white hover:bg-[#1a2c47] hover:border-[#3a5680] active:bg-[#1f3557] transition-colors"
              title={orgOpen ? 'Hide the organization choices' : 'Change organization / demo company'}
            >
              <Building2 size={14} className="text-cyan-300" />
              <span className="truncate max-w-[260px]">{orgLabel}</span>
              <ChevronDown size={13} className={cn('text-[#7d93b8] transition-transform', orgOpen && 'rotate-180')} />
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-lg border border-[#33507a] bg-[#16273f] px-3 py-2 text-[13.5px] font-semibold text-white">
              <Building2 size={14} className="text-cyan-300" />
              <span className="truncate max-w-[260px]">{orgLabel}</span>
            </span>
          )}
          <span
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-cyan-400/40 bg-[#101d31] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-200 flex-shrink-0"
            title={modeConfig.description}
          >
            {modeConfig.label}
          </span>
        </div>
        {/* Org content — demo picks a Company, sandbox picks an Organization.
            Collapsed behind the chip; one click away. */}
        {hasOrgSection && orgOpen && (
          <div className="mt-3 pt-3 border-t border-[#22334f]">
            <DemoProfilePicker />
            <SandboxOrgPicker
              options={sandboxOrgOptions}
              loading={sandboxOrgsLoading}
              activeOrganizationId={activeOrganizationId}
              onPick={(id) => {
                if (id) setOrgOpen(false) // choice made → collapse ("Show all" keeps it open)
                onPickSandboxOrg(id)
              }}
            />
          </div>
        )}
      </div>
      {/* Lifecycle rail — the same 5 modes, dashed + nested under the tunnel. */}
      <div className="rounded-xl border border-dashed border-[#33507a] px-3.5 py-3">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#6f86ac]">
          Lifecycle — modes run inside this organization
        </p>
        <ContextModeSwitcher />
      </div>
    </div>
  )
}

/**
 * SandboxOrgPicker — sandbox's parallel to demo's company tiles, shown inside the
 * Workspace card so you choose the org right on the launchpad (not only in the
 * portfolio sub-view). Uses the curated sandbox org list from the parent (orgs
 * that own a portfolio and aren't demo companies; Mach12 platform org first).
 */
function SandboxOrgPicker({
  options,
  loading,
  activeOrganizationId,
  onPick,
}: {
  options: Array<{ id: string; name: string }>
  loading: boolean
  activeOrganizationId: string | null
  onPick: (id: string | null) => void
}) {
  const appMode = useAppStore((s) => s.appMode)
  if (appMode !== 'sandbox') return null
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Building2 size={13} className="text-[#7d93b8]" />
          <h3 className="text-[12px] font-display font-semibold text-[#cfe0f5]">Organization</h3>
        </div>
        {activeOrganizationId && (
          <button onClick={() => onPick(null)} className="text-[10px] text-[#7d93b8] hover:text-white">Show all</button>
        )}
      </div>
      {options.length === 0 ? (
        // Loaded-but-empty is a real state, not a spinner — say so.
        <p className="text-[11px] text-[#7d93b8]">
          {loading ? 'Loading organizations…' : 'No sandbox organizations with portfolio data yet — build one here, or switch to Demo for the demo companies.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {options.map((o) => {
            const isActive = o.id === activeOrganizationId
            const isPlatform = o.id === 'org_mach12'
            return (
              <button
                key={o.id}
                onClick={() => onPick(o.id)}
                className={cn(
                  'text-left flex items-start gap-2 px-2 py-1.5 rounded-md border transition-all',
                  isActive ? 'border-brand-400 bg-brand-500/20 ring-1 ring-brand-400/40'
                  : isPlatform ? 'border-amber-400/50 bg-amber-400/10 hover:bg-amber-400/15'
                  : 'border-[#2a3f60] bg-[#101d31] hover:bg-[#16273f] hover:border-[#3a5680]',
                )}
              >
                <Building2 size={13} className={cn('mt-0.5 flex-shrink-0', isActive ? 'text-cyan-300' : isPlatform ? 'text-amber-300' : 'text-[#7d93b8]')} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <div className={cn('text-[11px] font-semibold truncate', isActive ? 'text-white' : 'text-[#e8f0fc]')}>{o.name}</div>
                    {isPlatform && <span className="text-[8px] uppercase tracking-wide font-bold text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded px-1 flex-shrink-0">Platform</span>}
                  </div>
                </div>
                {isActive && <Check size={11} className="text-cyan-300 flex-shrink-0 mt-0.5" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const managementContexts: { key: ManagementContext; label: string; description: string; icon: React.ReactNode; color: string }[] = [
  { key: 'portfolio', label: 'Portfolio Management Studio', description: 'Dashboard, budgets, resources, scenarios, priorities, and strategic analysis', icon: <FolderTree size={28} />, color: 'bg-brand-50 text-brand-600' },
  { key: 'project', label: 'Project Management Studio', description: 'Active projects, scheduling, earned value, finance, and project reports', icon: <FolderKanban size={28} />, color: 'bg-slate-50 text-slate-600' },
  { key: 'analytics', label: 'Analytics Studio', description: 'Conversational analytics across SAP S/4HANA and Tesseract: ask in plain language, get tables, AI-chosen charts, and saveable HTML or PDF reports', icon: <ChartNoAxesCombined size={28} />, color: 'bg-sky-50 text-sky-600' },
  { key: 'contract', label: 'Contract Studio', description: 'Master contracts, task orders, CLINs, and WBS mappings', icon: <FileText size={28} />, color: 'bg-blue-50 text-blue-600' },
  { key: 'organizational', label: 'Organization Studio', description: 'Organization hierarchy, resource pools, and capacity planning', icon: <Building2 size={28} />, color: 'bg-amber-50 text-amber-600' },
  { key: 'supplychain', label: 'Supply Chain Studio', description: 'Contract-aware, GPD-native planning: demand, supply, inventory, capacity, order promising, control tower, compliance, sustainment, and S&OP', icon: <PackageSearch size={28} />, color: 'bg-teal-50 text-teal-600' },
  { key: 'timesheet', label: 'Timesheet Studio', description: 'Weekly time entry, submissions, approvals queue, and supervisor review', icon: <Clock size={28} />, color: 'bg-emerald-50 text-emerald-600' },
  { key: 'configuration', label: 'Configuration Studio', description: 'SAP integration, field mappings, sync monitoring, and system connectivity', icon: <Settings size={28} />, color: 'bg-purple-50 text-purple-600' },
  { key: 'mywork', label: 'My Work', description: 'AI-powered personal command center. Briefing, priorities, approvals, and a portal into every other context tied to you', icon: <Inbox size={28} />, color: 'bg-cyan-50 text-cyan-600' },
  { key: 'personnel', label: 'Personnel Studio', description: 'HCM user directory, portfolio owner pool, and personnel attributes for project assignments', icon: <User size={28} />, color: 'bg-rose-50 text-rose-600' },
  { key: 'workflow-management', label: 'Workflow & Forms Studio', description: 'Design workflows and forms, route by form data, and govern approvals across every context', icon: <Workflow size={28} />, color: 'bg-indigo-50 text-indigo-600' },
  { key: 'cost', label: 'Cost Management Studio', description: 'Direct and rate-based allocations, cost transfers with approval, cost pools and rate segments, and CAS incurred cost reporting from the universal ledger', icon: <Calculator size={28} />, color: 'bg-orange-50 text-orange-600' },
  { key: 'crm', label: 'CRM Studio', description: 'Accounts, contacts, and the interaction log on the live SAP customer master, with pipeline, quoting, and Customer 360 landing in later phases', icon: <Handshake size={28} />, color: 'bg-lime-50 text-lime-700' },
  { key: 'it-ticketing', label: 'IT Ticketing Studio', description: 'IT help-desk ticketing: requests, workflow approvals, delegations, and reporting, ported from the standalone support-ticketing app', icon: <Ticket size={28} />, color: 'bg-fuchsia-50 text-fuchsia-700' },
]

// Placeholder tiles rendered alongside the real contexts. Selecting one
// is a no-op — the tile shows a "Coming soon" badge until the module ships.
// Empty today: the Construction Studio placeholder was replaced by the shipped
// Cost Management Studio tile above. Add a tile here when the next module is
// announced but not yet buildable.
const comingSoonContexts: { key: string; label: string; description: string; icon: React.ReactNode; color: string }[] = []

// ============================================================
// Portfolio Picker Components
// ============================================================

function PortfolioNode({ portfolio, depth = 0, searchQuery = '', expandedIds, onToggle }: {
  portfolio: Portfolio & { children?: Portfolio[] }; depth?: number; searchQuery?: string
  expandedIds: Set<string>; onToggle: (id: string) => void
}) {
  const navigate = useNavigate()
  const selectPortfolio = useAppStore((s) => s.selectPortfolio)

  const hasChildren = portfolio.children && portfolio.children.length > 0
  const isExpanded = expandedIds.has(portfolio.id)

  function handleSelect() {
    selectPortfolio(portfolio)
    navigate('/dashboard')
  }

  function handleToggle(e: React.MouseEvent) {
    e.stopPropagation()
    onToggle(portfolio.id)
  }

  const matchesSearch = !searchQuery || portfolio.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    portfolio.description?.toLowerCase().includes(searchQuery.toLowerCase())
  const childMatches = portfolio.children?.some(c => nodeMatchesSearch(c as Portfolio & { children?: Portfolio[] }, searchQuery)) ?? false

  // When searching, force-expand parents of matches and auto-show matching nodes
  const forceExpanded = searchQuery && childMatches

  if (searchQuery && !matchesSearch && !childMatches) return null

  const showChildren = hasChildren && (forceExpanded || isExpanded)

  return (
    <div>
      <div className="flex items-center gap-1 min-w-0" style={{ marginLeft: `${depth * 24}px` }}>
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={handleToggle}
            className="w-7 h-7 rounded-md flex items-center justify-center text-text-tertiary hover:text-brand-600 hover:bg-brand-50 transition-colors flex-shrink-0"
            title={showChildren ? 'Collapse' : 'Expand'}
          >
            {showChildren ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <div className="w-7 flex-shrink-0" />
        )}

        {/* Portfolio card */}
        <button
          onClick={handleSelect}
          className={cn(
            'flex-1 min-w-0 text-left flex items-center gap-3 px-4 py-3 rounded-lg',
            'border border-border hover:border-brand-400 hover:shadow-card-hover',
            'transition-all duration-200 group bg-white',
            searchQuery && matchesSearch ? 'ring-2 ring-brand-500/20' : '',
          )}
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-50 text-brand-600">
            {hasChildren ? <FolderTree size={20} /> : <Briefcase size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] text-text-tertiary">{portfolio.code}</span>
              <span className="text-body-md font-semibold text-text-primary group-hover:text-brand-600 transition-colors">
                {portfolio.name}
              </span>
            </div>
            <div className="text-body-sm text-text-secondary truncate">
              {portfolio.description}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-body-sm text-text-tertiary">
                {portfolio.kpis.activeProjects} projects
              </span>
              {portfolio.kpis.atRiskProjects > 0 && (
                <span className="badge-yellow text-[10px]">
                  {portfolio.kpis.atRiskProjects} at risk
                </span>
              )}
              {hasChildren && (
                <span className="text-[10px] uppercase font-medium tracking-wider text-brand-500">
                  {portfolio.children!.length} sub-portfolios
                </span>
              )}
            </div>
          </div>
          <ChevronRight size={16} className="text-text-tertiary group-hover:text-brand-500 transition-colors" />
        </button>
      </div>

      {showChildren && (
        <div className="mt-1 space-y-1">
          {portfolio.children!.map((child) => (
            <PortfolioNode key={child.id} portfolio={child as Portfolio & { children?: Portfolio[] }} depth={depth + 1} searchQuery={searchQuery} expandedIds={expandedIds} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  )
}

function nodeMatchesSearch(node: Portfolio & { children?: Portfolio[] }, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  if (node.name.toLowerCase().includes(q) || node.description?.toLowerCase().includes(q)) return true
  return node.children?.some(c => nodeMatchesSearch(c as Portfolio & { children?: Portfolio[] }, q)) ?? false
}

// ============================================================
// Project Picker Components
// ============================================================

type ProjectStatusFilter = 'All' | 'In Progress' | 'Planning' | 'At Risk' | 'On Hold' | 'Completed'
type ProjectSortKey = 'name' | 'status' | 'health' | 'budget' | 'progress'

const statusFilters: ProjectStatusFilter[] = ['All', 'In Progress', 'Planning', 'At Risk', 'On Hold', 'Completed']

function getHealthDot(health: string) {
  const colors: Record<string, string> = {
    Green: 'bg-status-green',
    Yellow: 'bg-status-yellow',
    Red: 'bg-status-red',
  }
  return <span className={cn('inline-block w-2.5 h-2.5 rounded-full', colors[health] ?? 'bg-gray-300')} />
}

function ProjectCard({ project, portfolioName, onSelect }: { project: Project; portfolioName: string; onSelect: () => void }) {
  const budgetPercent = project.budget.total > 0
    ? Math.round((project.budget.spent / project.budget.total) * 100)
    : 0

  return (
    <div className="relative">
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left flex flex-col gap-3 p-5 rounded-lg',
        'border border-border hover:border-slate-400 hover:shadow-card-hover',
        'transition-all duration-200 group bg-white',
      )}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-50 text-slate-600">
          <FolderKanban size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-body-md font-semibold text-text-primary group-hover:text-slate-600 transition-colors truncate">
              {project.name}
            </span>
            {getHealthDot(project.healthStatus)}
          </div>
          <div className="text-body-sm text-text-secondary truncate mt-0.5">
            {project.description}
          </div>
        </div>
        <ChevronRight size={16} className="text-text-tertiary group-hover:text-slate-500 transition-colors flex-shrink-0 mt-1" />
      </div>

      {/* Metadata row */}
      <div className="flex items-center gap-4 flex-wrap text-[12px] text-text-tertiary">
        <span className="font-mono text-text-secondary">{project.code}</span>
        <span className={getStatusBadgeClass(project.status) + ' text-[10px]'}>{project.status}</span>
        <span className="flex items-center gap-1">
          <User size={12} />
          {project.projectManagerName}
        </span>
        <span className="flex items-center gap-1">
          <Briefcase size={12} />
          {portfolioName}
        </span>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-4 gap-3">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-text-tertiary mb-0.5">Budget</span>
          <span className="text-body-sm font-semibold text-text-primary">{formatCompactCurrency(project.budget.total)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-text-tertiary mb-0.5">Spent</span>
          <span className="text-body-sm font-semibold text-text-primary">{budgetPercent}%</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-text-tertiary mb-0.5">SPI</span>
          <span className={cn('text-body-sm font-semibold', project.schedule.spi >= 0.95 ? 'text-status-green' : project.schedule.spi >= 0.85 ? 'text-status-yellow' : 'text-status-red')}>
            {project.schedule.spi.toFixed(2)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-text-tertiary mb-0.5">Progress</span>
          <span className="text-body-sm font-semibold text-text-primary">{project.schedule.percentComplete}%</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-surface-muted rounded-full h-1.5">
        <div
          className={cn(
            'h-1.5 rounded-full transition-all',
            project.healthStatus === 'Green' ? 'bg-status-green' : project.healthStatus === 'Yellow' ? 'bg-status-yellow' : 'bg-status-red',
          )}
          style={{ width: `${Math.min(project.schedule.percentComplete, 100)}%` }}
        />
      </div>
    </button>
    </div>
  )
}

function NewProjectTypePicker({ onBack, onChoose }: { onBack: () => void; onChoose: (kind: 'direct' | 'indirect') => void }) {
  const types: Array<{
    kind: 'direct' | 'indirect'
    label: string
    badge: string
    description: string
    bullets: string[]
    icon: React.ReactNode
    accent: string
  }> = [
    {
      kind: 'direct',
      label: 'Direct Project',
      badge: 'Customer-facing',
      description: 'Revenue-bearing work executed under a customer contract. Funded by the contract, not the portfolio budget.',
      bullets: ['Linked to a sales order, master contract, or CLIN', 'Earned value and billing recognized to the contract', 'Schedule baseline activates the project for delivery'],
      icon: <Briefcase size={28} />,
      accent: 'bg-blue-50 text-blue-600 border-blue-200',
    },
    {
      kind: 'indirect',
      label: 'Indirect Project',
      badge: 'Internal / Overhead',
      description: 'Internal initiative funded by the portfolio budget. Submitted as a request, prioritized, and approved through portfolio governance.',
      bullets: ['Funded from portfolio fiscal-year releases', 'Scored against strategic priorities and KPIs', 'Submitted as a request for portfolio approval'],
      icon: <FolderKanban size={28} />,
      accent: 'bg-amber-50 text-amber-600 border-amber-200',
    },
  ]

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-body-sm text-slate-600 hover:text-slate-700 font-medium">
          <ArrowLeft size={16} /> Back to projects
        </button>
      </div>

      <h2 className="text-heading-lg mb-1">What kind of project are you starting?</h2>
      <p className="text-body-sm text-text-secondary mb-6">
        Direct projects bill against a customer contract. Indirect projects are internal initiatives funded by the portfolio.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {types.map((t) => (
          <button
            key={t.kind}
            onClick={() => onChoose(t.kind)}
            className={cn(
              'text-left flex flex-col gap-3 p-6 rounded-xl bg-white',
              'border border-border hover:border-slate-400 hover:shadow-card-hover',
              'transition-all duration-200 group',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className={cn('w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border', t.accent)}>
                {t.icon}
              </div>
              <span className={cn('text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border', t.accent)}>
                {t.badge}
              </span>
            </div>
            <div>
              <div className="text-heading-md font-display group-hover:text-slate-600 transition-colors">{t.label}</div>
              <p className="text-body-sm text-text-secondary mt-1 leading-relaxed">{t.description}</p>
            </div>
            <ul className="space-y-1.5 mt-1">
              {t.bullets.map((b) => (
                <li key={b} className="text-body-sm text-text-secondary flex items-start gap-2">
                  <Check size={14} className="text-status-green flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-end gap-1 text-body-sm text-slate-600 font-medium mt-1 group-hover:text-slate-700">
              Start {t.label} <ChevronRight size={14} />
            </div>
          </button>
        ))}
      </div>
    </>
  )
}

function ProjectPicker({ onBack }: { onBack: () => void }) {
  const navigate = useNavigate()
  const selectProject = useAppStore((s) => s.selectProject)
  const selectPortfolio = useAppStore((s) => s.selectPortfolio)
  const { data: allProjects, isLoading: projectsLoading } = useProjects()
  const { data: portfolios } = usePortfolios()
  // Approved with project access, but no projects ticked yet (allowed_projects = {}).
  const noProjectsAssigned = useProjectEntitlementStore((s) => s.projectIds !== null && s.projectIds.length === 0)

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilter>('All')
  const [sortKey, setSortKey] = useState<ProjectSortKey>('name')
  const [showExplain, setShowExplain] = useState(false)
  const [pickingType, setPickingType] = useState(false)

  // Build portfolio name lookup
  const portfolioNames = useMemo(() => {
    const map = new Map<string, string>()
    portfolios?.forEach(p => map.set(p.id, p.name))
    return map
  }, [portfolios])

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    if (!allProjects) return []

    // Archived projects are soft-deleted: keep them out of the picker (mirrors
    // the in-portfolio ProjectList, which hides archived by default). Restore is
    // done from a portfolio's Projects list or the project detail header.
    let filtered = allProjects.filter(p => !p.archivedAt)

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(p => p.status === statusFilter)
    }

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(p =>
        (p.name ?? '').toLowerCase().includes(q) ||
        (p.code ?? '').toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        (p.projectManagerName ?? '').toLowerCase().includes(q) ||
        (portfolioNames.get(p.portfolioId) ?? '').toLowerCase().includes(q)
      )
    }

    // Sort
    const sorted = [...filtered]
    switch (sortKey) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'status':
        sorted.sort((a, b) => a.status.localeCompare(b.status))
        break
      case 'health': {
        const order: Record<string, number> = { Red: 0, Yellow: 1, Green: 2 }
        sorted.sort((a, b) => (order[a.healthStatus] ?? 3) - (order[b.healthStatus] ?? 3))
        break
      }
      case 'budget':
        sorted.sort((a, b) => b.budget.total - a.budget.total)
        break
      case 'progress':
        sorted.sort((a, b) => b.schedule.percentComplete - a.schedule.percentComplete)
        break
    }

    return sorted
  }, [allProjects, statusFilter, searchQuery, sortKey, portfolioNames])

  function handleProjectSelect(project: Project) {
    // Find the portfolio for this project so we set both contexts
    const portfolio = portfolios?.find(p => p.id === project.portfolioId) ?? null
    selectPortfolio(portfolio)
    selectProject(project)
    navigate(`/projects/${project.id}`)
  }

  // Summary stats
  const stats = useMemo(() => {
    if (!allProjects) return null
    const active = allProjects.filter(p => !p.archivedAt)
    return {
      total: active.length,
      inProgress: active.filter(p => p.status === 'In Progress').length,
      atRisk: active.filter(p => p.healthStatus === 'Red' || p.healthStatus === 'Yellow').length,
      totalBudget: active.reduce((sum, p) => sum + p.budget.total, 0),
    }
  }, [allProjects])

  if (pickingType) {
    return <NewProjectTypePicker onBack={() => setPickingType(false)} onChoose={(kind) => navigate(`/projects/new/${kind}`)} />
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-body-sm text-slate-600 hover:text-slate-700 font-medium">
          <ArrowLeft size={16} /> Back to contexts
        </button>
        <div className="flex items-center gap-2">
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setPickingType(true)}>
            Add Project
          </Button>
          <button onClick={() => setShowExplain(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-body-sm text-text-secondary hover:text-slate-600 hover:border-slate-400 transition-colors">
            <HelpCircle size={14} /> Explain this page
          </button>
        </div>
      </div>

      <h2 className="text-heading-lg mb-4">Select a Project</h2>

      {/* Summary cards — clickable to filter */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <button
            onClick={() => { setStatusFilter('All'); setSearchQuery('') }}
            className={cn(
              'text-left bg-white rounded-lg border p-3 transition-all hover:shadow-card-hover',
              statusFilter === 'All' && !searchQuery ? 'border-slate-400 ring-2 ring-slate-200' : 'border-border hover:border-slate-300'
            )}
          >
            <div className="text-[10px] uppercase tracking-wider text-text-tertiary">Total Projects</div>
            <div className="text-heading-lg font-display mt-1">{stats.total}</div>
          </button>
          <button
            onClick={() => { setStatusFilter('In Progress'); setSearchQuery('') }}
            className={cn(
              'text-left bg-white rounded-lg border p-3 transition-all hover:shadow-card-hover',
              statusFilter === 'In Progress' ? 'border-slate-400 ring-2 ring-slate-200' : 'border-border hover:border-slate-300'
            )}
          >
            <div className="text-[10px] uppercase tracking-wider text-text-tertiary">In Progress</div>
            <div className="text-heading-lg font-display mt-1 text-slate-600">{stats.inProgress}</div>
          </button>
          <button
            onClick={() => { setStatusFilter('At Risk'); setSearchQuery('') }}
            className={cn(
              'text-left bg-white rounded-lg border p-3 transition-all hover:shadow-card-hover',
              statusFilter === 'At Risk' ? 'border-red-400 ring-2 ring-red-200' : 'border-border hover:border-slate-300'
            )}
          >
            <div className="text-[10px] uppercase tracking-wider text-text-tertiary">At Risk</div>
            <div className="text-heading-lg font-display mt-1 text-status-red">{stats.atRisk}</div>
          </button>
          <button
            onClick={() => { setSortKey('budget'); setStatusFilter('All'); setSearchQuery('') }}
            className={cn(
              'text-left bg-white rounded-lg border p-3 transition-all hover:shadow-card-hover',
              sortKey === 'budget' && statusFilter === 'All' ? 'border-slate-400 ring-2 ring-slate-200' : 'border-border hover:border-slate-300'
            )}
          >
            <div className="text-[10px] uppercase tracking-wider text-text-tertiary">Total Budget</div>
            <div className="text-heading-lg font-display mt-1">{formatCompactCurrency(stats.totalBudget)}</div>
          </button>
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="search"
            placeholder="Search by name, code, PM, or portfolio..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-white text-body-sm focus:outline-none focus:ring-2 focus:ring-slate-500/30 focus:border-slate-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-text-tertiary" />
          <div className="flex rounded-lg border border-border overflow-hidden bg-white">
            {statusFilters.map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={cn(
                  'px-3 py-1.5 text-[12px] font-medium transition-colors',
                  statusFilter === f
                    ? 'bg-slate-600 text-white'
                    : 'text-text-secondary hover:bg-surface-muted'
                )}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-2 mb-4 text-[12px] text-text-tertiary">
        <span>Sort by:</span>
        {(['name', 'health', 'budget', 'progress', 'status'] as ProjectSortKey[]).map(key => (
          <button
            key={key}
            onClick={() => setSortKey(key)}
            className={cn(
              'px-2 py-1 rounded transition-colors capitalize',
              sortKey === key ? 'bg-slate-100 text-slate-700 font-medium' : 'hover:bg-surface-muted'
            )}
          >
            {key}
          </button>
        ))}
        <span className="ml-auto text-text-tertiary">
          {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Project list */}
      {projectsLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-36 rounded-lg skeleton" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProjects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              portfolioName={portfolioNames.get(project.portfolioId) ?? 'Unknown'}
              onSelect={() => handleProjectSelect(project)}
            />
          ))}
          {filteredProjects.length === 0 && (
            <div className="text-center py-12 text-body-sm text-text-secondary">
              {searchQuery || statusFilter !== 'All'
                ? <>No projects match your filters. Try adjusting the search or status filter.</>
                : noProjectsAssigned
                  ? <>No projects have been assigned to you yet. Your administrator chooses which projects you can see.</>
                  : <>No projects found.</>
              }
            </div>
          )}
        </div>
      )}

      {/* Explain panel overlay */}
      {showExplain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowExplain(false)}>
          <div onClick={e => e.stopPropagation()}>
            <ProjectExplainPanel onClose={() => setShowExplain(false)} />
          </div>
        </div>
      )}
    </>
  )
}

// ============================================================
// Explain This Page Panels
// ============================================================

function ExplainPanel({ onClose }: { onClose: () => void }) {
  // Lists only the contexts this user can open (deployment + entitlement).
  const entitlement = useModuleEntitlement()
  return (
    <div className="bg-white rounded-xl border border-border shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <HelpCircle size={18} className="text-brand-600" />
          <h3 className="text-heading-md font-display">Understanding the Portfolio Selector</h3>
        </div>
        <button onClick={onClose} aria-label="Close help" className="p-1.5 rounded-lg hover:bg-surface-muted transition-colors"><X size={16} /></button>
      </div>
      <div className="px-6 py-5 space-y-5">
        <div>
          <h4 className="text-heading-sm font-display mb-2">Portfolio Hierarchy &amp; Budgeting</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-4 rounded-lg border border-brand-200 bg-brand-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center">
                  <FolderTree size={16} />
                </div>
                <span className="text-body-sm font-semibold text-brand-800">Top-Level Portfolios</span>
              </div>
              <p className="text-[12px] text-brand-700 leading-relaxed">
                Top-level portfolios <strong>receive budget from organizations</strong>. They can allocate budget to child portfolios and directly manage projects, resources, risks, and workflows.
              </p>
            </div>
            <div className="p-4 rounded-lg border border-brand-200 bg-brand-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center">
                  <Briefcase size={16} />
                </div>
                <span className="text-body-sm font-semibold text-brand-800">Child Portfolios</span>
              </div>
              <p className="text-[12px] text-brand-700 leading-relaxed">
                Child portfolios <strong>pull budget from their parent portfolio</strong>. They have the same features as top-level portfolios &mdash; projects, resources, risks, workflows &mdash; but their funding comes from the parent rather than directly from organizations.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-heading-sm font-display mb-2">Navigating the Hierarchy</h4>
          <p className="text-body-sm text-text-secondary leading-relaxed">
            Portfolios are organized in a <strong>tree hierarchy</strong>. A top-level portfolio may contain child portfolios beneath it. When you select any portfolio, you enter its management context and see its dashboard, projects, budgets, and analytics.
          </p>
          <div className="mt-3 bg-surface-muted rounded-lg p-3">
            <p className="text-[12px] text-text-secondary">
              <strong>Drill-through:</strong> Click any portfolio in the tree to select it. Portfolios with children will show aggregated metrics from all descendant portfolios. You can navigate between sibling and child portfolios using the portfolio breadcrumb in the sidebar.
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-heading-sm font-display mb-2">Finding Your Portfolio</h4>
          <p className="text-body-sm text-text-secondary leading-relaxed">
            Use the <strong>search bar</strong> above the portfolio tree to filter by name or description. The tree will collapse to show only matching portfolios and their parent path. This is useful when you manage a specific portfolio within a large hierarchy.
          </p>
          <div className="mt-3 bg-surface-muted rounded-lg p-3">
            <p className="text-[12px] text-text-secondary">
              <strong>Tip:</strong> If you do not see a portfolio you expect, check with your administrator. Portfolio visibility is controlled by your role assignments and organizational access. Your assigned portfolios are based on your user profile.
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-heading-sm font-display mb-2">Management Contexts</h4>
          <p className="text-body-sm text-text-secondary leading-relaxed mb-3">
            The context selector on the previous screen lets you choose which management area to work in. Each context provides specialized tools:
          </p>
          <div className="grid grid-cols-2 gap-2">
            {managementContexts.filter((ctx) => isContextVisible(ctx.key, entitlement)).map(ctx => (
              <div key={ctx.key} className="flex items-start gap-2 p-2 rounded-lg bg-surface-muted/50">
                <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[14px]', ctx.color)}>
                  {ctx.icon}
                </div>
                <div>
                  <div className="text-[11px] font-semibold">{ctx.label}</div>
                  <div className="text-[10px] text-text-tertiary">{ctx.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ProjectExplainPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-border shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <HelpCircle size={18} className="text-slate-600" />
          <h3 className="text-heading-md font-display">Understanding the Project Selector</h3>
        </div>
        <button onClick={onClose} aria-label="Close help" className="p-1.5 rounded-lg hover:bg-surface-muted transition-colors"><X size={16} /></button>
      </div>
      <div className="px-6 py-5 space-y-5">
        <div>
          <h4 className="text-heading-sm font-display mb-2">Project Management Context</h4>
          <p className="text-body-sm text-text-secondary leading-relaxed">
            The project management context focuses on <strong>individual project execution</strong>. Select a project to access its schedule, earned value metrics, financial data, WBS hierarchy, and detailed reporting tools.
          </p>
        </div>

        <div>
          <h4 className="text-heading-sm font-display mb-2">Project Health Indicators</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg border border-green-200 bg-green-50/50">
              <div className="flex items-center gap-2 mb-1">
                {getHealthDot('Green')}
                <span className="text-body-sm font-semibold text-green-800">On Track</span>
              </div>
              <p className="text-[12px] text-green-700">SPI and CPI are within acceptable thresholds. No critical risks.</p>
            </div>
            <div className="p-3 rounded-lg border border-yellow-200 bg-yellow-50/50">
              <div className="flex items-center gap-2 mb-1">
                {getHealthDot('Yellow')}
                <span className="text-body-sm font-semibold text-yellow-800">Needs Attention</span>
              </div>
              <p className="text-[12px] text-yellow-700">Schedule or cost variance detected. Corrective action may be needed.</p>
            </div>
            <div className="p-3 rounded-lg border border-red-200 bg-red-50/50">
              <div className="flex items-center gap-2 mb-1">
                {getHealthDot('Red')}
                <span className="text-body-sm font-semibold text-red-800">At Risk</span>
              </div>
              <p className="text-[12px] text-red-700">Significant issues with schedule, cost, or scope. Immediate action required.</p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-heading-sm font-display mb-2">Key Metrics</h4>
          <div className="space-y-2">
            <div className="flex items-start gap-2 p-2 rounded-lg bg-surface-muted/50">
              <Activity size={14} className="text-slate-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[11px] font-semibold">SPI (Schedule Performance Index)</div>
                <div className="text-[10px] text-text-tertiary">Ratio of earned value to planned value. 1.0 = on schedule, &lt;1.0 = behind schedule.</div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-2 rounded-lg bg-surface-muted/50">
              <DollarSign size={14} className="text-slate-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[11px] font-semibold">Budget Utilization</div>
                <div className="text-[10px] text-text-tertiary">Percentage of total budget that has been spent. Helps identify underspend or overspend.</div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-2 rounded-lg bg-surface-muted/50">
              <Calendar size={14} className="text-slate-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[11px] font-semibold">Progress</div>
                <div className="text-[10px] text-text-tertiary">Overall completion percentage based on the scheduling method (traditional WBS or agile sprints).</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-heading-sm font-display mb-2">Available Tools</h4>
          <p className="text-body-sm text-text-secondary leading-relaxed mb-2">
            Once you select a project, the sidebar will show project-specific tools:
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Project Detail', desc: 'KPIs, budget breakdown, active risks' },
              { label: 'Scheduling', desc: 'Schedule visualization with dependencies' },
              { label: 'Baseline Management', desc: 'Cost baselines, burden rates, schedule imports' },
              { label: 'Forecast Management', desc: 'EAC models, variance analysis, projections' },
              { label: 'Progress & EV', desc: 'EVM baselines, S-curves, forecasting' },
              { label: 'Hierarchy', desc: 'WBS structure and work packages' },
              { label: 'Project Reports', desc: 'Analytics and status reporting' },
            ].map(t => (
              <div key={t.label} className="flex items-start gap-2 p-2 rounded-lg bg-surface-muted/50">
                <div>
                  <div className="text-[11px] font-semibold">{t.label}</div>
                  <div className="text-[10px] text-text-tertiary">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Main ContextSelector
// ============================================================

// SandboxQuickStart was lifted out of this file 2026-05-28 — it now
// lives at `web/src/components/onboarding/SandboxQuickStart.tsx` with
// a 3-card variant (Create / Import / Clone a demo) that pairs with
// the demo packs system.

export function ContextSelector() {
  const navigate = useNavigate()
  const entitlement = useModuleEntitlement()
  const managementContext = useAppStore((s) => s.managementContext)
  const setManagementContext = useAppStore((s) => s.setManagementContext)
  const selectPortfolio = useAppStore((s) => s.selectPortfolio)
  const appMode = useAppStore((s) => s.appMode)
  const setAppMode = useAppStore((s) => s.setAppMode)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const setActiveOrganization = useAppStore((s) => s.setActiveOrganization)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const { data: hierarchy, isLoading } = usePortfolioHierarchy()

  // Slice 3 (homepage IA) — the "you are here" workspace summary, shown as a
  // compact chip in the hero (anchor) + a scope pill over the module grid, so
  // WHAT (modules) is visibly tied to WHERE (org + environment). Mirrors the
  // in-app top-left anchor language.
  const workspaceMode = APP_MODE_CONFIGS[appMode]
  const workspaceCompany = getDemoCompany(currentCompanyId)
  // Tunnel continuity in the hero too: dev/quality/production inherit the
  // picked company instead of dropping to "No org selected" — the org frames
  // every mode (data-level scoping for those modes lands with the P1 collapse).
  const workspaceScope = appMode === 'demo'
    ? (workspaceCompany?.name ?? 'All companies')
    : appMode === 'sandbox'
      ? (activeOrganizationId ?? workspaceCompany?.name ?? 'No org selected')
      : (workspaceCompany?.name ?? activeOrganizationId ?? 'No org selected')
  // Slice 4 — re-render when client-preview toggles so the launchpad flips into a
  // clean product view (the internal Workspace picker + Client-view button hide
  // the instant preview starts). isInternalSurface() reads the preview store
  // synchronously; this subscription is what makes React re-run it.
  const previewKey = useSyncExternalStore(subscribePreviewClient, getPreviewClientKey, () => null)
  void previewKey
  const internalSurface = isInternalSurface()
  // Client view is available in EVERY mode (tunnel model): the org identity now
  // persists across the lifecycle, so "see the client's product" is valid at any
  // stage. The old appMode==='demo' gate predates identity continuity — it was
  // "demo-only" only because currentCompanyId used to exist only in demo.
  const canPreviewClient = internalSurface && !!currentCompanyId

  // Sandbox org picker — Sandbox's parallel to Demo's company tiles. Selecting
  // an org scopes the portfolio tree to it (see usePortfolioData). The curated
  // option list (orgs that own portfolios, minus demo companies, Mach12 first +
  // flagged) is shared with the in-app top-bar org dropdown (TunnelOrgChip) via
  // useSandboxOrgOptions, so both surfaces always offer the same choices.
  const { options: sandboxOrgOptions, loading: sandboxOrgsLoading } = useSandboxOrgOptions()
  const [showExplain, setShowExplain] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false)
  const [expandedContextKey, setExpandedContextKey] = useState<ManagementContext | null>(null)

  // Aggregate counts from the portfolio hierarchy for per-context metadata
  const contextMeta = useMemo(() => {
    const flat: Portfolio[] = []
    const walk = (nodes: Array<Portfolio & { children?: Portfolio[] }>) => {
      for (const n of nodes) {
        flat.push(n)
        if (n.children?.length) walk(n.children as Array<Portfolio & { children?: Portfolio[] }>)
      }
    }
    walk((hierarchy ?? []) as Array<Portfolio & { children?: Portfolio[] }>)
    const portfolioCount = flat.length
    const projectsActive = flat.reduce((s, p) => s + (p.kpis?.activeProjects ?? 0), 0)
    const projectsAtRisk = flat.reduce((s, p) => s + (p.kpis?.atRiskProjects ?? 0), 0)
    return { portfolioCount, projectsActive, projectsAtRisk }
  }, [hierarchy])

  function metaForContext(key: ManagementContext): { label: string; tone?: 'risk' | 'muted' }[] {
    const { portfolioCount, projectsActive, projectsAtRisk } = contextMeta
    switch (key) {
      case 'portfolio':
        return portfolioCount > 0 ? [{ label: `${portfolioCount} ${portfolioCount === 1 ? 'portfolio' : 'portfolios'}` }] : []
      case 'project': {
        const out: { label: string; tone?: 'risk' | 'muted' }[] = []
        if (projectsActive > 0) out.push({ label: `${projectsActive} active` })
        if (projectsAtRisk > 0) out.push({ label: `${projectsAtRisk} at risk`, tone: 'risk' })
        return out
      }
      default:
        return []
    }
  }

  // Default-expand every portfolio in the tree once the hierarchy loads.
  // Runs once per hierarchy fetch; after that the user owns expand/collapse.
  useEffect(() => {
    if (!hierarchy || hasAutoExpanded) return
    const ids = new Set<string>()
    const collect = (nodes: Array<Portfolio & { children?: Portfolio[] }>) => {
      for (const n of nodes) {
        ids.add(n.id)
        if (n.children && n.children.length > 0) collect(n.children as Array<Portfolio & { children?: Portfolio[] }>)
      }
    }
    collect(hierarchy as Array<Portfolio & { children?: Portfolio[] }>)
    setExpandedIds(ids)
    setHasAutoExpanded(true)
  }, [hierarchy, hasAutoExpanded])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  function handleContextSelect(ctx: ManagementContext) {
    // Contract Management is delivered by the standalone Mach12 CLM app
    // (clm.mach12.ai), not an in-app context. Open it in a new tab and leave
    // the Tesseract Overview in place. Handled before setManagementContext so
    // the app does not switch into an internal contract context.
    if (ctx === 'contract') {
      window.open('https://clm.mach12.ai', '_blank', 'noopener,noreferrer')
      return
    }
    setManagementContext(ctx)
    if (ctx === 'configuration') {
      // Configuration context lands on the Configuration Dashboard
      navigate('/config')
    } else if (ctx === 'supplychain') {
      // Supply Chain context — land on the demand dashboard
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/supply-chain')
    } else if (ctx === 'timesheet') {
      // Timesheet context — supervisor dashboard is the landing page
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/timesheet')
    } else if (ctx === 'mywork') {
      // My Work context — personal AI command center. Skip portfolio picker.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/my-work')
    } else if (ctx === 'personnel') {
      // Personnel Management — HCM directory. No portfolio scope needed.
      navigate('/personnel')
    } else if (ctx === 'workflow-management') {
      // Workflow Management — org-wide authoring/governance surface. Seed a
      // portfolio for portfolio-scoped queries but land on the module home.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/workflow-management')
    } else if (ctx === 'cost') {
      // Cost Management — org-wide cost accounting surface (allocations, burden
      // rates, transfers, pools, incurred cost reporting). Seed a portfolio for
      // portfolio-scoped queries but land on the module home.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/cost')
    } else if (ctx === 'crm') {
      // CRM Studio - org-wide revenue front end (accounts, contacts,
      // interactions). Seed a portfolio for portfolio-scoped queries but land
      // on the studio home.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/crm')
    } else if (ctx === 'it-ticketing') {
      // IT Ticketing Studio - org-wide help-desk surface. Seed a portfolio for
      // portfolio-scoped queries but land on the studio home.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/it-ticketing')
    } else if (ctx === 'analytics') {
      // Analytics Studio — the conversational analytics workspace. Seed the
      // first portfolio so portfolio-scoped asks have a default context, but
      // the engine itself spans all SAP and Tesseract data.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/analytics')
    } else if (ctx === 'organizational') {
      // Organizational Management — the over-arching Organizations layer (slice 2).
      // Lands on the Org Hierarchy home (set the active organization + see the tree);
      // the org sidebar then offers Org Directory + People. Replaces the prior
      // direct-to-org-directory jump so the tile opens the section, not one page.
      navigate('/organizations')
    } else if (ctx !== 'portfolio' && ctx !== 'project') {
      // Catch-all: select the first portfolio and land on the portfolio dashboard.
      if (hierarchy && hierarchy.length > 0) {
        selectPortfolio(hierarchy[0] ?? null)
      }
      navigate('/dashboard')
    }
  }

  function handleBack() {
    setManagementContext(null)
    setSearchQuery('')
  }

  const showPortfolioPicker = managementContext === 'portfolio'
  const showProjectPicker = managementContext === 'project'
  const showPicker = showPortfolioPicker || showProjectPicker

  const heroTitle = showProjectPicker
    ? 'Select Your Project'
    : showPortfolioPicker
    ? 'Select Your Portfolio'
    : `Welcome to ${appFullName()}`

  const heroSubtitle = showProjectPicker
    ? 'Choose a project to access its schedule, earned value, finance, and detailed reporting tools.'
    : showPortfolioPicker
    ? 'Choose a portfolio to view its dashboard, projects, budgets, and analytics.'
    : 'Choose a management context to get started. Each context provides specialized tools and views for your role.'

  return (
    <div className="min-h-screen bg-surface-muted flex">
      {/* Empty sidebar + context header — the picker's version of the module
          sidebar's context indicator. Click anywhere (the ⇄) to go home. */}
      {showPicker && (
        <aside className={cn('w-60 flex-shrink-0 flex flex-col text-white', showProjectPicker ? 'bg-slate-900' : 'bg-brand-800')}>
          <div className="flex items-center gap-2 h-12 px-3 border-b border-white/10">
            <svg width="24" height="24" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <g transform="translate(20, 20)">
                <rect x="-14" y="-14" width="28" height="28" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.6"/>
                <rect x="-6.5" y="-6.5" width="13" height="13" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.6"/>
                <rect x="-2.5" y="-2.5" width="5" height="5" rx="0.8" fill="white" opacity="0.92"/>
              </g>
            </svg>
            <span className="font-display font-bold text-[11px] tracking-wide">TESSERACT <span className="text-cyan-400">XPM</span></span>
          </div>
          <button
            onClick={handleBack}
            className="w-full text-left px-3 py-2 border-b border-white/10 hover:bg-white/5 transition-colors group"
            aria-label="Back to home"
            title="Back to home"
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-wider text-white/50">{showProjectPicker ? 'Project' : 'Portfolio'} Context</span>
              <ArrowLeftRight size={12} className="text-white/30 group-hover:text-white/70 transition-colors" />
            </div>
            <div className="text-body-sm font-medium truncate">Select {showProjectPicker ? 'a project' : 'a portfolio'}</div>
            <div className="text-[10px] mt-0.5 uppercase tracking-wider text-white/50">Back to home</div>
          </button>
        </aside>
      )}
      <div className="flex-1 flex flex-col min-w-0">
      {/* Hero header */}
      <div className={cn(showProjectPicker ? 'bg-slate-800' : 'bg-brand-800', 'text-white relative overflow-hidden')}>
        <div className="absolute inset-0 pointer-events-none opacity-60 [background:radial-gradient(80%_120%_at_85%_-10%,rgba(56,189,248,0.22),transparent_60%),radial-gradient(60%_100%_at_-5%_120%,rgba(168,85,247,0.15),transparent_55%)]" aria-hidden />
        <div className="max-w-5xl mx-auto px-6 pt-6 pb-16 relative">
          {/* Top-right account / sign-in entry */}
          <div className="absolute top-6 right-6 z-20">
            <AccountMenu />
          </div>
          {!showPicker && (
          <div className="flex items-center gap-2 mb-1">
            <svg width="28" height="28" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <defs><linearGradient id="ctx-g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#2563EB"/><stop offset="100%" stopColor="#06B6D4"/></linearGradient></defs>
              <g transform="translate(20, 20)">
                <rect x="-14" y="-14" width="28" height="28" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.4"/>
                <rect x="-6.5" y="-6.5" width="13" height="13" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.4"/>
                <line x1="-14" y1="-14" x2="-6.5" y2="-6.5" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8"/>
                <line x1="14" y1="-14" x2="6.5" y2="-6.5" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8"/>
                <line x1="14" y1="14" x2="6.5" y2="6.5" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8"/>
                <line x1="-14" y1="14" x2="-6.5" y2="6.5" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8"/>
                <polygon points="-14,-14 14,-14 6.5,-6.5 -6.5,-6.5" fill="rgba(255,255,255,0.12)"/>
                <polygon points="14,-14 14,14 6.5,6.5 6.5,-6.5" fill="rgba(255,255,255,0.08)"/>
                <polygon points="-14,14 14,14 6.5,6.5 -6.5,6.5" fill="rgba(255,255,255,0.06)"/>
                <polygon points="-14,-14 -14,14 -6.5,6.5 -6.5,-6.5" fill="rgba(255,255,255,0.1)"/>
                <rect x="-2.5" y="-2.5" width="5" height="5" rx="0.8" fill="white" opacity="0.92"/>
              </g>
            </svg>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-bold text-sm tracking-wide">{APP_IDENTITY.name.toUpperCase()}{APP_IDENTITY.productSuffix && <> <span className="text-cyan-300">{APP_IDENTITY.productSuffix}</span></>}</span>
            </div>
          </div>
          )}
          <h1 className="font-display text-display-md mt-3 tracking-tight">{heroTitle}</h1>
          <p className="text-white/70 text-body-md mt-1.5 max-w-2xl">{heroSubtitle}</p>
          {/* Workspace anchor chip (slice 3) — the "you are here" cue in the hero,
              mirroring the in-app top-left org anchor: environment + org at a glance,
              before you pick a module below. */}
          {!showPicker && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-2 rounded-lg bg-white/10 border border-white/20 px-3 py-1.5">
                <Building2 size={14} className="text-cyan-300 flex-shrink-0" />
                <span className="text-body-sm font-semibold text-white truncate max-w-[220px]" title={workspaceScope}>{workspaceScope}</span>
                <span className="text-white/30" aria-hidden>·</span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/25 text-white/90">{workspaceMode.label}</span>
              </div>
              {/* Client view (slice 4) — launch the chrome-free product view for
                  THIS company straight from the launchpad (internal surface, any
                  mode). Exit lives in the global EnvironmentBanner. */}
              {canPreviewClient && (
                <button
                  onClick={() => enterClientPreview(currentCompanyId as string)}
                  title="Preview this as the client's live product — hides the internal cockpit"
                  aria-label="Switch to client product view"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-400/15 border border-cyan-300/40 text-cyan-100 hover:bg-cyan-400/25 active:scale-95 transition-colors px-3 py-1.5 text-body-sm font-medium"
                >
                  <Eye size={14} /> Client view
                </button>
              )}
              {/* Exit client view — the homepage has no EnvironmentBanner (that
                  only renders in AppShell/module pages), so entering preview here
                  would otherwise strand you. This is the launchpad's own exit. */}
              {previewKey && (
                <button
                  onClick={() => exitClientPreview()}
                  title="Exit the client product view and return to the internal cockpit"
                  aria-label="Exit client view"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 border border-white/30 text-white hover:bg-white/25 active:scale-95 transition-colors px-3 py-1.5 text-body-sm font-medium"
                >
                  <X size={14} /> Exit client view
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Workspace — the tunnel, straight under the hero (above the feature
          cards): pick WHO + the lifecycle before anything else. Its own dark
          canvas panel, so it reads as part of the hero region. */}
      {!showPicker && internalSurface && (
        <div className="max-w-5xl mx-auto px-6 -mt-10 mb-3 w-full relative z-10">
          <div className="rounded-2xl border border-[#1d2c44] bg-[#0b1626] p-4 shadow-card">
            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#7d93b8] mb-2 flex items-center gap-1.5">
              <Building2 size={12} /> Workspace
            </div>
            <WorkspacePanel
              sandboxOrgOptions={sandboxOrgOptions}
              sandboxOrgsLoading={sandboxOrgsLoading}
              activeOrganizationId={activeOrganizationId}
              onPickSandboxOrg={setActiveOrganization}
            />
          </div>
        </div>
      )}

      {/* Feature highlights */}
      {!showPicker && (
        <div className={cn('max-w-5xl mx-auto px-6 mb-3 w-full relative z-10', !internalSurface && '-mt-10')}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { icon: <TrendingUp size={16} />, title: 'Real-time KPIs', desc: 'Live portfolio health, SPI, CPI metrics' },
              { icon: <Shield size={16} />, title: 'Risk Intelligence', desc: 'AI-powered risk detection and recommendations' },
              { icon: <Sparkles size={16} />, title: 'XPM Agent', desc: 'Orchestrate autonomous agents that execute project functions on a schedule' },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-lg border border-border px-3 py-2 shadow-card flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center flex-shrink-0">
                  {f.icon}
                </div>
                <div className="min-w-0">
                  <div className="text-body-sm font-semibold leading-tight">{f.title}</div>
                  <div className="text-[11px] text-text-secondary mt-0.5 leading-tight">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-6 pb-4 w-full flex-1 flex flex-col min-h-0">
        {showProjectPicker ? (
          <ProjectPicker onBack={handleBack} />
        ) : showPortfolioPicker ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <button onClick={handleBack} className="flex items-center gap-1 text-body-sm text-brand-600 hover:text-brand-700 font-medium">
                <ArrowLeft size={16} /> Back to contexts
              </button>

              <div className="flex items-center gap-2">
                <Button variant="primary" icon={<Plus size={14} />} onClick={() => navigate('/portfolios/new')}>
                  Create Portfolio
                </Button>
                <Button variant="secondary" icon={<HelpCircle size={14} />} onClick={() => setShowExplain(true)}>
                  Explain this page
                </Button>
              </div>
            </div>

            <h2 className="text-heading-lg mb-4">Portfolio Hierarchy</h2>

            {/* Sandbox org picker — focus the sandbox on one organization (e.g.
                Mach12, which has no demo-profile tile on the homepage). */}
            {appMode === 'sandbox' && (
              <div
                className={cn(
                  'mb-4 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3',
                  activeOrganizationId
                    ? 'border-amber-300 bg-amber-50/60 ring-1 ring-amber-200'
                    : 'border-border bg-surface-muted',
                )}
              >
                <div className="flex items-center gap-2">
                  <Building2 size={16} className={activeOrganizationId ? 'text-amber-700' : 'text-text-tertiary'} />
                  <span className="text-body-sm font-medium text-text-primary">Organization</span>
                </div>
                <select
                  value={activeOrganizationId ?? ''}
                  onChange={(e) => setActiveOrganization(e.target.value || null)}
                  className="h-9 min-w-[16rem] rounded-lg border border-border bg-white px-3 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                >
                  <option value="">All organizations</option>
                  {sandboxOrgOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.id === 'org_mach12' ? `★ ${o.name} (Platform)` : o.name}
                    </option>
                  ))}
                </select>
                {activeOrganizationId && (
                  <button
                    onClick={() => setActiveOrganization(null)}
                    className="text-[12px] font-medium text-amber-700 hover:text-amber-900 transition-colors"
                  >
                    Clear · show all
                  </button>
                )}
                <span className="text-[12px] text-text-tertiary">
                  {activeOrganizationId ? 'Scoped to this organization' : 'Showing every organization'}
                </span>
              </div>
            )}

            {/* Search bar */}
            <div className="relative w-full max-w-md mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="search"
                placeholder="Search portfolios by name or description..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-white text-body-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
              />
            </div>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-lg skeleton" />
                ))}
              </div>
            ) : appMode === 'sandbox' && (hierarchy?.length ?? 0) === 0 && !searchQuery ? (
              <SandboxQuickStart
                onCreatePortfolio={() => navigate('/portfolios/new')}
                onSwitchToDemo={() => setAppMode('demo')}
              />
            ) : (
              <div className="space-y-2">
                {hierarchy?.map((p) => (
                  <PortfolioNode key={p.id} portfolio={p as Portfolio & { children?: Portfolio[] }} searchQuery={searchQuery} expandedIds={expandedIds} onToggle={toggleExpanded} />
                ))}
                {searchQuery && hierarchy?.every(p => !nodeMatchesSearch(p as Portfolio & { children?: Portfolio[] }, searchQuery)) && (
                  <div className="text-center py-8 text-body-sm text-text-secondary">
                    No portfolios match &quot;{searchQuery}&quot;
                  </div>
                )}

                {/* Create portfolio CTA below hierarchy */}
                {!isLoading && (
                  <button
                    onClick={() => navigate('/portfolios/new')}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-4 rounded-lg mt-3',
                      'border border-dashed border-brand-300 hover:border-brand-500 hover:bg-brand-50/50',
                      'transition-all duration-200 group',
                    )}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-50 text-brand-400 group-hover:text-brand-600 group-hover:bg-brand-100 transition-colors">
                      <Plus size={20} />
                    </div>
                    <div className="text-left">
                      <div className="text-body-sm font-medium text-brand-500 group-hover:text-brand-600 transition-colors">Create a new portfolio</div>
                      <div className="text-[12px] text-text-tertiary">Set up a new portfolio with AI-guided configuration</div>
                    </div>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Workspace (the tunnel) moved above the feature cards — it now
                renders in the hero region so WHERE + lifecycle precede WHAT. */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h2 className="text-heading-sm font-display">Choose Management Context</h2>
              {/* Scope pill (slice 3) — ties WHAT (the modules below) to WHERE
                  (the workspace above): each tile opens into this org + environment. */}
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-text-secondary bg-surface-muted border border-border rounded-full px-2 py-0.5">
                <Building2 size={11} className="text-text-tertiary" />
                scoped to {workspaceScope}
                <span className="text-text-tertiary">·</span>
                {workspaceMode.label}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 flex-1 min-h-0">
              {managementContexts.filter((ctx) => isContextVisible(ctx.key, entitlement)).map((ctx) => {
                const isExpanded = expandedContextKey === ctx.key
                const features = ctx.description.split(/,\s*|\sand\s/).filter(Boolean).map(s => s.trim())
                const meta = metaForContext(ctx.key)
                return (
                  <div
                    key={ctx.key}
                    onClick={() => handleContextSelect(ctx.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleContextSelect(ctx.key) }}
                    className={cn(
                      'h-full text-left flex flex-col rounded-lg cursor-pointer',
                      'border border-border hover:border-brand-400 hover:shadow-card-hover',
                      'transition-all duration-200 group bg-white overflow-hidden',
                    )}
                  >
                    <div className="flex items-start gap-3 px-4 pt-3 pb-2 flex-1">
                      <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 [&_svg]:w-[24px] [&_svg]:h-[24px]', ctx.color)}>
                        {ctx.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-heading-sm font-semibold font-display group-hover:text-brand-600 transition-colors leading-tight">
                          {ctx.label}
                        </div>
                        <div className={cn('text-[12px] text-text-secondary leading-snug mt-0.5', !isExpanded && 'line-clamp-2')}>
                          {ctx.description}
                        </div>
                        {meta.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {meta.map((m, i) => (
                              <span
                                key={i}
                                className={cn(
                                  'text-[11px] font-medium px-1.5 py-0.5 rounded-md border',
                                  m.tone === 'risk'
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-slate-50 text-text-secondary border-slate-200',
                                )}
                              >
                                {m.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {isExpanded && (
                      <div
                        className="px-4 pb-2 border-t border-border/60 pt-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary mb-1">What's inside</div>
                        <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {features.map((f) => (
                            <li key={f} className="text-body-sm text-text-secondary flex items-start gap-1.5">
                              <span className="text-brand-500 mt-0.5">•</span>
                              <span className="capitalize">{f}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-2 flex justify-end">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); handleContextSelect(ctx.key) }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleContextSelect(ctx.key) } }}
                            className="text-body-sm font-medium text-brand-600 hover:text-brand-700 cursor-pointer"
                          >
                            Open {ctx.label} →
                          </span>
                        </div>
                      </div>
                    )}
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                      aria-expanded={isExpanded}
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedContextKey(isExpanded ? null : ctx.key)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          setExpandedContextKey(isExpanded ? null : ctx.key)
                        }
                      }}
                      className="flex items-center justify-center py-1 border-t border-border/60 text-text-tertiary hover:text-brand-500 hover:bg-surface-muted/60 transition-colors"
                    >
                      <ChevronDown
                        size={18}
                        className={cn('transition-transform duration-200', isExpanded && 'rotate-180')}
                      />
                    </div>
                  </div>
                )
              })}
              {/* Coming-soon placeholder tiles — visually present so users
                  can see the roadmap, but non-interactive until the
                  underlying modules ship. */}
              {comingSoonContexts.map((ctx) => (
                <div
                  key={ctx.key}
                  className={cn(
                    'h-full text-left flex flex-col rounded-lg cursor-not-allowed',
                    'border border-dashed border-slate-300 bg-slate-50/50 opacity-90',
                    'overflow-hidden',
                  )}
                  aria-disabled
                >
                  <div className="flex items-start gap-3 px-4 pt-3 pb-3 flex-1">
                    <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 [&_svg]:w-[24px] [&_svg]:h-[24px]', ctx.color)}>
                      {ctx.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-heading-sm font-semibold font-display leading-tight text-text-secondary">
                          {ctx.label}
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                          Coming soon
                        </span>
                      </div>
                      <div className="text-[12px] text-text-tertiary leading-snug mt-0.5 line-clamp-2">
                        {ctx.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>

      {/* Explain panel overlay (portfolio) */}
      {showExplain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowExplain(false)}>
          <div onClick={e => e.stopPropagation()}>
            <ExplainPanel onClose={() => setShowExplain(false)} />
          </div>
        </div>
      )}

      {/* Version */}
      <div className="py-3 text-center text-[11px] text-text-tertiary/50" title={APP_VERSION_FULL}>
        v{APP_VERSION_FULL}
      </div>
      </div>
    </div>
  )
}
